/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import { useSetKountReady } from '@/providers/kount-ready';
import type { KountSessionResponse } from '@/routes/action.kount-session';

/**
 * CHECKOUT-17 — Kount Device Data Collector mount.
 *
 * Mounted once at the CHECKOUT LAYOUT ROOT (inside `<KountReadyProvider/>` in
 * `<CheckoutFormPage/>`), NOT inside the Payment step's `<ToggleCardEdit/>`.
 * Mounting inside the payment toggle was the previous design but broke for
 * PayPal Express: `onApprove` advances `step → PLACE_ORDER` in well under the
 * 5-second collect window, the toggle collapses, the collector unmounts, its
 * `useEffect` cleanup runs `clearTimeout(timeoutId)`, and the safety timer
 * never fires — leaving `kountReady` stuck `false` and blocking the Place
 * Order CTA for the entire PayPal Express flow (credit-card flow didn't hit
 * this because the shopper spends more than 5s typing card details). Living
 * at the layout root, the collector survives every checkout step transition.
 *
 * On mount:
 *
 *   1. POSTs to `/action/kount-session` (which proxies to INT-22's
 *      `POST /custom/ashley-kount/v1/session`). The server-side mint also
 *      persists `basket.custom.kountSessionId` inside a Transaction so the
 *      order hook reads the same id at place-order time — the storefront
 *      does NOT need a follow-up basket PATCH.
 *   2. If the action returns `{ enabled: false }` (Kount gated off) or any
 *      error, fires `setKountReady(true)` immediately so the Place Order
 *      CTA is never blocked when Kount is disabled.
 *   3. Otherwise injects a 1×1 off-screen `aria-hidden` iframe sourced from
 *      the Kount-published DDC SDK URL with the minted session id, listens
 *      for the SDK's `collected` postMessage event filtered on
 *      `event.origin === <ddcUrl host>`, and on collection fires
 *      `setKountReady(true)`.
 *   4. A hard 5-second timeout flips `kountReady` to `true` even if the
 *      SDK never posts — slow / blocked SDK must not block legitimate
 *      checkout. INT-22's RIS hook is the ultimate fraud gate; on degraded
 *      DDC the order still places and Kount scores on payload-only
 *      signals.
 *
 * Security gate (CHECKOUT-17 explicit): props are deliberately limited to
 * non-PII configuration. Card data, PAN, and Kount API keys are never
 * passed through this component — the unmasked PAN is owned by the
 * Elavon Converge LightBox and is tokenised before it leaves the iframe;
 * the Kount API key lives on the BM service credential and never reaches
 * the React bundle.
 */

const DEFAULT_TIMEOUT_MS = 5000;

/**
 * Kount DDC postMessage type filter. Locked once the Kount onboarding pack
 * confirms the exact event shape — kept as a single constant so any change is
 * a one-file diff. See CHECKOUT-17's `todos.hardcoded` row.
 */
const COLLECTED_MESSAGE_TYPE = 'collected';

const TEST_ID = 'kount-ddc';

interface KountDataCollectorProps {
    /** Optional callback fired when DDC reports collection. Useful for analytics. */
    onCollected?: () => void;
    /** Override the default 5-second collection timeout (Storybook / tests). */
    timeoutMs?: number;
}

interface CollectedMessage {
    type: string;
    sessionId?: string;
}

function isCollectedMessage(value: unknown): value is CollectedMessage {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as { type?: unknown };
    return typeof candidate.type === 'string';
}

/** Extract the URL origin from a full DDC SDK URL. Returns null on parse failure. */
function originOf(url: string): string | null {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
}

export default function KountDataCollector({ onCollected, timeoutMs = DEFAULT_TIMEOUT_MS }: KountDataCollectorProps) {
    const setKountReady = useSetKountReady();
    const [session, setSession] = useState<KountSessionResponse | null>(null);

    // Refs guard against React-19 strict-mode double-invocation and parent re-renders:
    // the /session call must fire exactly once, and the iframe / timeout / listener
    // must each be installed exactly once for the lifetime of the component.
    const fetchedRef = useRef(false);
    const completedRef = useRef(false);
    const setKountReadyRef = useRef(setKountReady);
    const onCollectedRef = useRef(onCollected);
    setKountReadyRef.current = setKountReady;
    onCollectedRef.current = onCollected;

    // Step 1 — fire `/action/kount-session` exactly once per component instance.
    //
    // React 19 StrictMode runs every effect twice in dev (mount → cleanup → mount).
    // We deliberately do NOT install an AbortController or `cancelled` flag here:
    //
    //  - `fetchedRef` already guarantees we never fire two parallel requests.
    //  - `completedRef` already guarantees `setKountReady(true)` fires at most once.
    //  - Calling `setSession` on an unmounted component is a no-op in React 19.
    //
    // Aborting on cleanup would race with StrictMode — the first pass aborts the
    // fetch, the second pass refuses to fire a new one (because `fetchedRef` is
    // already true), and the gate stays permanently closed for every dev session.
    // RIS on the cartridge side remains the ultimate fraud verdict regardless of
    // what the browser does here.
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;

        const completeReady = () => {
            if (completedRef.current) return;
            completedRef.current = true;
            setKountReadyRef.current(true);
        };

        fetch('/action/kount-session', {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (res) => {
                if (!res.ok) {
                    // Any failure (404 no basket, 502 SCAPI error, 401 token) opens the gate so
                    // the shopper is never permanently blocked.
                    completeReady();
                    setSession(null);
                    return;
                }
                const data = (await res.json()) as KountSessionResponse;
                setSession(data);
                if (data.enabled === false) {
                    // Kount gated off — open the gate immediately, no iframe to mount.
                    completeReady();
                }
            })
            .catch(() => {
                completeReady();
                setSession(null);
            });
    }, []);

    // Step 2 — once we have an enabled session, install the postMessage listener
    // and the timeout. Both flip `kountReady` exactly once via `completedRef`.
    const enabled = session?.enabled === true ? session : null;
    const ddcOrigin = enabled ? originOf(enabled.ddcUrl) : null;

    useEffect(() => {
        if (!enabled || !ddcOrigin) return;

        const completeReady = () => {
            if (completedRef.current) return;
            completedRef.current = true;
            onCollectedRef.current?.();
            setKountReadyRef.current(true);
        };

        const handleMessage = (event: MessageEvent) => {
            // Origin filter is the only trustworthy guard against a malicious cross-origin
            // frame posting a forged `collected` message. Drop anything not from the
            // Kount-published DDC origin.
            if (event.origin !== ddcOrigin) return;
            if (!isCollectedMessage(event.data)) return;
            if (event.data.type !== COLLECTED_MESSAGE_TYPE) return;
            completeReady();
        };

        window.addEventListener('message', handleMessage);

        const timeoutId = window.setTimeout(() => {
            // Slow / blocked SDK — open the gate so the shopper can place the order. RIS
            // still runs on the cartridge side; that order is scored on payload-only
            // signals (legacy parity for SDK outages).
            completeReady();
        }, timeoutMs);

        return () => {
            window.removeEventListener('message', handleMessage);
            window.clearTimeout(timeoutId);
        };
    }, [enabled, ddcOrigin, timeoutMs]);

    // Render null on disabled / error / not-yet-loaded — preserves "no iframe in the DOM"
    // when Kount is gated off (CHECKOUT-17 acceptance criterion).
    if (!enabled) return null;

    return (
        <iframe
            data-testid={TEST_ID}
            title="Kount DDC"
            src={enabled.ddcUrl}
            aria-hidden="true"
            tabIndex={-1}
            // Kount publishes the DDC iframe as 1×1; we position it off-screen so it never
            // affects layout, and `aria-hidden` keeps screen readers from announcing it.
            style={{
                position: 'absolute',
                left: '-9999px',
                top: '-9999px',
                width: '1px',
                height: '1px',
                border: 0,
                pointerEvents: 'none',
            }}
        />
    );
}
